"use client"

import moment from "moment"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { MdKeyboardArrowDown } from "react-icons/md"
import { useDispatch, useSelector } from "react-redux"
import { updateApplicationStatus } from "../../../../redux/features/adminLeadSlice"
import {
  fetchChecklistDetails,
  selectCurrentChecklist,
  clearCurrentChecklist,
} from "../../../../redux/features/clientChecklistSlice"
import {
  fetchBankDetailsByCountry,
  selectCurrentBankDetails,
  selectIsLoading as selectBankDetailsLoading,
} from "../../../../redux/features/clientBankDetail"
import { motion } from "framer-motion"
import apiFetch from "../../../../utlis/apiFetch"

export default function InvoiceTab({ selectedData, onRefresh }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const checklist = useSelector(selectCurrentChecklist)
  const bankDetails = useSelector(selectCurrentBankDetails)
  const isBankDetailsLoading = useSelector(selectBankDetailsLoading)

  useEffect(() => {
    dispatch(clearCurrentChecklist())
  }, [dispatch])

  useEffect(() => {
    if (selectedData?.countryOfInterest && selectedData?.visaCategory) {
      dispatch(
        fetchChecklistDetails({
          country: selectedData.countryOfInterest,
          visaCategory: selectedData.visaCategory,
        }),
      )
      dispatch(fetchBankDetailsByCountry(selectedData.countryOfInterest))
    }
  }, [selectedData?.countryOfInterest, selectedData?.visaCategory, dispatch])

  useEffect(() => {
    if (checklist?.fee !== undefined && checklist?.fee !== null) {
      setFormData((prev) => ({ ...prev, amount: checklist.fee }))
    }
  }, [checklist])

  const [success, setSuccess] = useState(false)
  const user = localStorage.getItem("user")
  const userData = JSON.parse(user)
  const [formData, setFormData] = useState({
    clientName: selectedData?.firstName + " " + selectedData?.lastName,
    applicationNo: selectedData?.uniqueId,
    paymentType: "",
    paymentReceived: "",
    amount: "",
    transactionDate: "",
    transactionId: "",
    proofOfPayment: null,
    fileName: "",
  })

  const [paymentStatus, setPaymentStatus] = useState(selectedData?.paymentStatus)
  const [originalStatus, setOriginalStatus] = useState(selectedData?.paymentStatus)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [emailSuccess, setEmailSuccess] = useState(false)

  useEffect(() => {
    if (selectedData?.invoice) {
      setFormData((prev) => ({
        ...prev,
        transactionDate: selectedData.invoice.transactionDate
          ? moment(selectedData.invoice.transactionDate).format("DD/MM/YY")
          : moment().format("DD/MM/YY"),
        transactionId: selectedData.invoice.transactionId || "",
        fileName: selectedData.invoice.uploadedFiles?.name || "",
      }))
    }

    if (selectedData?.paymentStatus) {
      setPaymentStatus(selectedData.paymentStatus)
      setOriginalStatus(selectedData.paymentStatus)
    }
  }, [selectedData])

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData((prev) => ({
        ...prev,
        proofOfPayment: file,
      }))
    }
  }

  const handleDownload = () => {
    if (selectedData?.invoice?.uploadedFiles?.location) {
      window.open(selectedData.invoice.uploadedFiles.location, "_blank")
    }
  }

  // const handleViewInvoice = () => {
  //   // Implementation for viewing invoice
  // }

  const handleStatusChange = (status) => {
    setPaymentStatus(status)
    setIsDropdownOpen(false)
  }

  const handleSaveStatus = async () => {
    if (originalStatus === "Paid") {
      setError("Cannot change payment status after payment is completed")
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(false)
    setEmailSuccess(false)

    try {
      const resultAction = await dispatch(updateApplicationStatus({ id: selectedData._id, paymentStatus }))

      if (updateApplicationStatus.fulfilled.match(resultAction)) {
        try {
          const token = localStorage.getItem("token")
          if (token) {
            const freshResponse = await apiFetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/application/getApplication/${selectedData._id}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              },
            )
            const freshData = freshResponse._data ?? await freshResponse.json()
            const latestApplication = freshData?.body || freshData?.application || freshData
            if (freshResponse.ok && typeof onRefresh === "function" && latestApplication?._id) {
              onRefresh(latestApplication)
            }
          }
        } catch (refreshError) {
          console.error("Failed to refresh invoice data:", refreshError)
        }

        setOriginalStatus(paymentStatus)
        setSuccess("Payment status changed to " + paymentStatus + " successfully")

        // If payment status is "Paid", send confirmation email
        if (paymentStatus === "Paid") {
          try {
            const token = localStorage.getItem("token")

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/application/confirmationEmail`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ applicationId: selectedData._id }),
            })

            if (!response.ok) {
              const errorText = await response.text()
              console.error("Failed to send confirmation email:", errorText)
              setError("Failed to send confirmation email: " + errorText)
            } else {
              setEmailSuccess("Email sent successfully to client and admin")
            }
          } catch (error) {
            console.error("Error sending confirmation email:", error)
            setError("Error sending confirmation email: " + error.message)
          }
        }
      } else if (resultAction.error) {
        setError("Failed to update payment status: " + resultAction.error.message)
      }
    } catch (err) {
      setError("An unexpected error occurred: " + err.message)
    } finally {
      setLoading(false)

      // Clear success and error messages after 5 seconds
      setTimeout(() => {
        setSuccess(false)
        setError(null)
        setEmailSuccess(false)
      }, 5000)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    // Implementation for form submission
  }

  const handlePayNow = () => {
    if (bankDetails) {
      const invoiceTransactionDate = selectedData?.invoice?.transactionDate
        ? moment(selectedData.invoice.transactionDate).format("YYYY-MM-DD")
        : ""
      navigate("/client/applications/form/payment", {
        state: {
          bankDetails,
          paymentInfo: {
            clientName: selectedData?.firstName + " " + selectedData?.lastName,
            applicationNo: selectedData?.uniqueId,
            amount: formData.amount || checklist?.fee,
            transactionDate: invoiceTransactionDate,
            transactionId: selectedData?.invoice?.transactionId || "",
            proofOfPayment: null,
            proofPreview: "",
            payStatus: "Not Paid",
            fileName: selectedData?.invoice?.uploadedFiles?.name || "",
          },
          countryOfInterest: selectedData?.countryOfInterest,
          applicationId: selectedData?._id,
          name: selectedData?.firstName + " " + selectedData?.lastName,
          applicationNo: selectedData?.uniqueId,
          amount: formData.amount || checklist?.fee,
          returnUrl: window.location.hash.replace("#", ""),
        },
      })
    }
  }

  const statusChanged = paymentStatus !== originalStatus

  return (
    <div className="bg-[#FFFBF5] border border-[#CAC3BB] p-3 md:p-6 rounded-b-lg text-sm md:text-base">
      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="mb-4 p-3 bg-green-100 text-green-700 rounded-md"
        >
          {success}
        </motion.div>
      )}

      {emailSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="mb-4 p-3 bg-green-100 text-green-700 rounded-md"
        >
          {emailSuccess}
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="mb-4 p-3 bg-red-100 text-red-700 rounded-md"
        >
          {error}
        </motion.div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {/* Left Column */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block font-medium text-gray-900">Client Name</label>
              <input
                type="text"
                value={formData.clientName}
                readOnly
                className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900"
              />
            </div>

            <div className="space-y-2">
              <label className="block font-medium text-gray-900">Application No.</label>
              <input
                type="text"
                value={formData.applicationNo}
                readOnly
                className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900"
              />
            </div>

            {/* <div className="space-y-2">
              <label className="block font-medium text-gray-900">Payment Type</label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.paymentType}
                  readOnly
                  className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900 pr-10"
                />
                <MdKeyboardArrowDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
              </div>
            </div> */}

            <div className="space-y-2">
              <label className="block font-medium text-gray-900">Payment Status</label>
              {userData?.role === "admin"||(userData?.role === "sub-admin" && JSON.parse(localStorage.getItem('user'))?.permissions?.includes('leads')) ? (
                <div className="relative">
                  <div
                    className={`w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900 pr-10 flex justify-between items-center ${
                      selectedData?.status === "Cancelled" || selectedData?.status === "WithdrawnByClient" || selectedData?.status === "WithdrawnByAgent"
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    onClick={() => {
                      if (selectedData?.status !== "Cancelled" && selectedData?.status !== "WithdrawnByClient" && selectedData?.status !== "WithdrawnByAgent" && paymentStatus !== "Paid") {
                        setIsDropdownOpen(!isDropdownOpen)
                      }
                    }}
                  >
                    <span>{paymentStatus}</span>
                    <MdKeyboardArrowDown className="text-gray-500" />
                  </div>

                  {isDropdownOpen && selectedData?.status !== "Cancelled" && selectedData?.status !== "WithdrawnByClient" && selectedData?.status !== "WithdrawnByAgent" && paymentStatus !== "Paid" && (
                    <div className="relative z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                      {["Paid", "Not Paid", "Pending"].map((status) => (
                        <div
                          key={status}
                          className="p-2.5 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleStatusChange(status)}
                        >
                          {status}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900 pr-10 flex justify-between items-center cursor-pointer">
                  <span>{paymentStatus}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block font-medium text-gray-900">Transaction Date</label>
              <input
                type="date"
                value={moment(formData.transactionDate, "DD/MM/YY").format("YYYY-MM-DD")}
                readOnly
                className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900"
              />
            </div>
            <div className="space-y-2">
              <label className="block font-medium text-gray-900">Transaction ID</label>
              <input
                type="text"
                value={formData.transactionId}
                readOnly
                className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900"
              />
            </div>

            <div className="space-y-2">
              <label className="block font-medium text-gray-900">Proof of Payment</label>
              <div className="flex justify-between items-center">
                {formData.fileName ? (
                  <>
                    <span className="text-blue-500 truncate max-w-[200px]">{formData.fileName}</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleDownload}
                        className="bg-[#005787] text-white px-4 py-2 rounded-full hover:bg-[#004a70] transition-colors"
                      >
                        Download
                      </button>
                      {selectedData?.paymentStatus !== "Paid" && userData?.role !== "admin" && (
                        <button
                          type="button"
                          onClick={handlePayNow}
                          disabled={!bankDetails || isBankDetailsLoading}
                          className={`px-4 py-2 rounded-full text-white ${
                            !bankDetails || isBankDetailsLoading
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-[#005787] hover:bg-[#004a70]"
                          }`}
                        >
                          {isBankDetailsLoading ? "Loading..." : "Update"}
                        </button>
                      )}
                    </div>
                  </>
                ) : userData?.role !== "admin" &&
                  selectedData?.paymentStatus !== "Pending" &&
                  selectedData?.paymentStatus !== "Paid" &&
                  selectedData?.status !== "Withdrawn" &&
                  selectedData?.status !== "Cancelled" &&
                  selectedData?.status !== "WithdrawnByClient" &&
                  selectedData?.status !== "WithdrawnByAgent" ? (
                  <button
                    type="button"
                    onClick={handlePayNow}
                    disabled={!bankDetails || isBankDetailsLoading}
                    className={`px-4 py-2 rounded-full text-white ${
                      !bankDetails || isBankDetailsLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-[#005787] hover:bg-[#004a70]"
                    }`}
                  >
                    {isBankDetailsLoading ? "Loading..." : "Upload Payment Proof"}
                  </button>
                ) : (
                  <div className="flex justify-end">
                    <div className="text-red-500">No file uploaded yet</div>
                  </div>
                )}
              </div>
              {isBankDetailsLoading && <div className="text-blue-600 text-sm mt-2">Loading bank details for payment...</div>}
              {!isBankDetailsLoading && !bankDetails && (
                <div className="text-red-600 text-sm mt-2">
                  Bank details not available for {selectedData?.countryOfInterest}. Payment cannot be processed.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          {statusChanged && selectedData?.status !== "Cancelled" && selectedData?.status !== "WithdrawnByClient" && selectedData?.status !== "WithdrawnByAgent" && originalStatus !== "Paid" && (
            <button
              type="button"
              onClick={handleSaveStatus}
              disabled={loading}
              className="bg-[#005787] text-white px-6 py-2 rounded-full transition-colors flex items-center justify-center"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}